/* eslint-disable react/jsx-props-no-spreading */
import React, { useCallback, useEffect, useState } from 'react'
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  styled,
  Tooltip,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { CircleErrorIcon, CloseIcon } from '@rsmus/myrsm-icons'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import { PbiReportDefinitionViewModel } from '@rsmus/ecp-userservice'
import api from '../../api'
import { Styles } from '../../types'
import ResponsiveModal from '../../rsmCoreComponents/components/ResponsiveModal'
import TextField from '../TextField/TextField'
import { tokens } from '../../styles/materialTheme'

type Mode = 'create' | 'edit'

interface PowerBIReportModalProps {
  open: boolean
  onClose: () => void
  onSave?: (report: PbiReportDefinitionViewModel) => void
  mode?: Mode
  initialData?: PbiReportDefinitionViewModel
  onUpdate?: (report: PbiReportDefinitionViewModel) => void
  onRemove?: (reportId: number) => void
  lockShortNameOnEdit?: boolean
}

const styles: Styles = {
  container: (theme) => ({
    padding: '1.5rem',
    [theme.breakpoints.down('tablet')]: {
      paddingBottom: '1.5rem',
    },
  }),
  fieldContainer: (theme) => ({
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: '1.5rem',
    [theme.breakpoints.only('mobile')]: {
      gridTemplateColumns: '1fr',
    },
  }),
  title: (theme) => ({
    fontFamily: 'Prelo-Book, sans-serif',
    fontSize: '2.25rem',
    lineHeight: '2.5rem',
    paddingBottom: '1.75rem',
    border: '0',
    [theme.breakpoints.only('mobile')]: {
      fontSize: '2rem',
      paddingTop: '1rem',
    },
  }),
  buttonContainer: {
    display: 'flex',
    flexDirection: 'row',
    flexFlow: 'wrap',
    justifyContent: 'center',
    alignItems: 'center',
    paddingTop: '1.5rem',
    gap: '0.625rem',
    border: '0',
  },
  errorHeader: (theme) => ({
    fontFamily: tokens.type.fontFamilies.preloBold,
    fontSize: '1.125rem',
    lineHeight: '1.75rem',
    [theme.breakpoints.only('mobile')]: {
      fontSize: '1.5rem',
      paddingTop: '1rem',
    },
  }),
  errorMessage: (theme) => ({
    fontFamily: tokens.type.fontFamilies.preloBook,
    fontSize: '1.125rem',
    lineHeight: '1.5rem',
    [theme.breakpoints.only('mobile')]: {
      fontSize: '1rem',
      paddingBottom: '1rem',
    },
  }),
  errorContainer: {
    width: '100%',
    height: '7.8125rem',
    padding: '1.5rem',
    backgroundColor: tokens.colors.warning.secondary,
    marginBottom: '1.5rem',
  },
  checkboxContainer: {
    display: 'flex',
    alignItems: 'center',
  },
}

const StyledLabel = styled('label')(({ theme }) => ({
  fontFamily: 'Prelo-Black, sans-serif',
  fontSize: '1rem',
  paddingBottom: '0.5rem',
  color: theme.palette.text.primary,
  display: 'block',
}))

/**
 * PowerBIReportModal component for creating and editing Power BI Report Definitions.
 * This modal handles the definition-level properties that are shared across report instances.
 * Instance-specific properties (DisplayName, WorkspaceId, ObjectId, etc.) are managed
 * separately in the PowerBIReportInstanceModal component.
 *
 * @param open - Whether the modal is open
 * @param onClose - Callback when modal is closed
 * @param onSave - Callback when a new report definition is saved
 * @param mode - 'create' or 'edit' mode
 * @param initialData - Initial data for edit mode
 * @param onUpdate - Callback when report definition is updated
 * @param onRemove - Callback when report definition is removed
 * @param lockShortNameOnEdit - Whether to lock the short name field in edit mode (default: true)
 */
const PowerBIReportModal = ({
  open,
  onClose,
  onSave,
  mode = 'create',
  initialData,
  onUpdate,
  onRemove,
  lockShortNameOnEdit = true,
}: PowerBIReportModalProps) => {
  const { t } = useTranslation()
  const methods = useForm({
    defaultValues: {
      reportShortName: '',
      cemAppId: '',
      clientSecretName: '',
      tenantId: '',
      servicePrincipalId: '',
      isClientLevelAuthorization: false,
    },
  })
  const [hasError, setHasError] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  // Track if report instances exist - used to disable IsClientLevelAuthorization toggle
  const [hasInstances, setHasInstances] = useState(false)
  const letterNumberHyphenRegex = /^[A-Za-z0-9\- ()]+$/
  const guidRegex =
    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/

  const formatGUID = (value: string) => value.toUpperCase().trim()

  /**
   * Effect to reset form and fetch instances count when modal opens.
   * In edit mode, checks if any instances exist to determine if
   * IsClientLevelAuthorization can be toggled.
   */

  useEffect(() => {
    const subscription = methods.watch(() => {
      if (hasError) {
        setHasError(false)
      }
    })

    return () => subscription.unsubscribe()
  }, [methods, hasError])

  useEffect(() => {
    if (!open) return
    setHasError(false)
    setIsSaving(false)
    setHasInstances(false)
    methods.clearErrors()
    if (mode !== 'edit' || !initialData) {
      methods.reset()
      return
    }

    // Helper function to pick first defined value from object keys
    const pick = (obj: any, ...keys: string[]) =>
      keys.reduce<any>((val, k) => val ?? obj?.[k], undefined)

    // Reset form with definition-specific values from initialData
    methods.reset({
      reportShortName: pick(initialData, 'reportName') ?? '',
      cemAppId: pick(initialData, 'cemAppId', 'appId') ?? '',
      clientSecretName:
        pick(initialData, 'clientSecretName', 'secretName') ?? '',
      tenantId: pick(initialData, 'tenantId') ?? '',
      servicePrincipalId:
        pick(initialData, 'servicePrincipalId', 'clientId') ?? '',
      isClientLevelAuthorization:
        pick(initialData, 'isClientLevelAuthorization') ?? false,
    })

    // Fetch instances count to determine if auth model can be changed
    const fetchInstancesCount = async () => {
      if (!initialData?.id) return
      try {
        const filter = {
          pageNumber: 1,
          pageSize: 1,
          sortBy: 'displayName',
          isSortAscending: true,
        }

        const response = await api.user.report_GetAllReportInstances(
          initialData.id,
          filter,
          undefined,
        )
        const instances = response?.data ?? []
        setHasInstances(instances.length > 0)
      } catch (error) {
        console.error('Failed to fetch report instances:', error)
        setHasInstances(false)
      }
    }
    fetchInstancesCount()
  }, [open, mode, initialData, methods])

  /**
   * Handles adding a new Power BI report definition.
   * Creates the report definition without instance-specific properties.
   */
  const handleAddNewReport = async () => {
    setHasError(false)
    setIsSaving(true)
    const isClientLevelAuthorization = methods.getValues(
      'isClientLevelAuthorization',
    )
    // NOTE: Using 'unknown' intermediate cast because AdminReportDataViewModel type
    // will be updated after NSwag regeneration to match the new PbiReportDefinition schema
    const report = {
      cemAppId: methods.getValues('cemAppId').trim(),
      servicePrincipalId: formatGUID(methods.getValues('servicePrincipalId')),
      reportName: methods.getValues('reportShortName').trim(),
      clientSecretName: methods.getValues('clientSecretName').trim(),
      tenantId: formatGUID(methods.getValues('tenantId')),
      isClientLevelAuthorization,
      id: 0,
      effectiveIdentityEnabled: false,
      effectiveIdentityRoleName: undefined,
    }

    await api.user
      .report_AddPowerBIReport(report)
      .then(() => {
        methods.reset()
        onSave?.(report)
      })
      .catch(() => {
        setHasError(true)
      })
      .finally(() => {
        setIsSaving(false)
      })
  }

  /**
   * Handles updating an existing Power BI report definition.
   * Updates definition-level properties only.
   */
  const handleUpdateReport = async () => {
    if (!initialData) return
    setHasError(false)
    setIsSaving(true)
    const isClientLevelAuthorization = methods.getValues(
      'isClientLevelAuthorization',
    )
    // NOTE: Using 'unknown' intermediate cast because AdminReportDataViewModel type
    // will be updated after NSwag regeneration to match the new PbiReportDefinition schema
    const report: PbiReportDefinitionViewModel = {
      ...initialData,
      cemAppId: methods.getValues('cemAppId').trim(),
      servicePrincipalId: formatGUID(methods.getValues('servicePrincipalId')),
      reportName: methods.getValues('reportShortName').trim(),
      clientSecretName: methods.getValues('clientSecretName').trim(),
      tenantId: formatGUID(methods.getValues('tenantId')),
      isClientLevelAuthorization,
      effectiveIdentityEnabled: initialData?.effectiveIdentityEnabled ?? false,
      effectiveIdentityRoleName: initialData?.effectiveIdentityRoleName,
    }

    await api.user
      .report_UpdatePowerBIReport(report)
      .catch(() => {
        setHasError(true)
      })
      .finally(() => {
        setIsSaving(false)
        methods.reset()
        onUpdate?.(report)
      })
  }

  const handleOnClose = () => {
    methods.reset()
    setHasError(false)
    onClose()
  }

  const handleCloseIconKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (handleOnClose && (event.key === 'Enter' || event.key === ' ')) {
        event.preventDefault() // prevent re-opening the dial when enter pressed
        handleOnClose()
      }
    },
    [handleOnClose],
  )

  /**
   * Validates that the input value is a valid GUID format.
   * @param value - The value to validate
   * @returns True if valid, error message if invalid
   */
  const guidValidation = (value: string) => {
    if (!guidRegex.test(value.trim().toUpperCase())) {
      return t('PowerBI.ReportModal.InvalidGUIDError')
    }

    return true
  }

  /**
   * Validates string input against a regex pattern.
   * @param value - The value to validate
   * @param errorText - Error message to return if validation fails
   * @param regex - Regex pattern to test against
   * @returns True if valid, error message if invalid
   */
  const stringValidation = (
    value: string,
    errorText: string,
    regex: RegExp,
  ) => {
    if (!regex.test(value)) {
      return errorText
    }
    return true
  }

  /**
   * Renders the IsClientLevelAuthorization checkbox with conditional tooltip.
   * The checkbox is disabled when instances exist, as the security model
   * cannot be changed while report instances are associated with the definition.
   */
  const renderIsClientLevelAuthorizationCheckbox = () => {
    const checkbox = (
      <Box>
        <StyledLabel htmlFor="isClientLevelAuthorization">
          {t('PowerBI.ReportModal.IsClientLevelAuthorization')}
        </StyledLabel>
        <Box sx={styles.checkboxContainer}>
          <Controller
            name="isClientLevelAuthorization"
            control={methods.control}
            render={({ field: { onChange, value, ref } }) => (
              <Checkbox
                id="isClientLevelAuthorization"
                inputRef={ref}
                checked={value}
                onChange={(e) => onChange(e.target.checked)}
                disabled={hasInstances}
                inputProps={{
                  'aria-describedby': 'isClientLevelAuthorizationDescription',
                }}
                data-testid="Chk_PowerBIReportModal_IsClientLevelAuthorization"
              />
            )}
          />
        </Box>
      </Box>
    )

    // Wrap with tooltip if instances exist to explain why it's disabled
    if (hasInstances) {
      return (
        <Tooltip
          title={t(
            'PowerBI.ReportModal.IsClientLevelAuthorizationDisabledTooltip',
          )}
          placement="top"
          arrow>
          <Box>{checkbox}</Box>
        </Tooltip>
      )
    }

    return checkbox
  }

  const title =
    mode === 'create'
      ? t('PowerBI.ReportModal.Header')
      : t('PowerBI.ReportModal.EditHeader', 'Edit Power BI report')

  const primaryLabel =
    mode === 'create'
      ? t('PowerBI.ReportModal.AddReport')
      : t('PowerBI.ReportModal.Save', 'Save')

  const onSubmit =
    mode === 'create' ? () => handleAddNewReport() : () => handleUpdateReport()

  return (
    <Box className="Rsm-ProfileModal">
      <ResponsiveModal
        open={open}
        onClose={handleOnClose}
        aria-label={title}
        desktopMaxHeight="40rem"
        removeHeaderSpacing
        removeCloseButton
        removeContentSpacing
        data-testid="Lbl_PowerBIReport_AddNewDialog_Modal">
        <FormProvider {...methods}>
          <Box
            component="form"
            sx={styles.container}
            onSubmit={methods.handleSubmit(onSubmit)}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <IconButton
                sx={{ padding: 0 }}
                tabIndex={0}
                aria-label={t('ModalDialog.CloseDialog')}
                data-testid="Btn_Modal_Close"
                onClick={handleOnClose}
                onKeyDown={(event: React.KeyboardEvent) =>
                  handleCloseIconKeyDown(event)
                }
                disableFocusRipple
                disableRipple>
                <CloseIcon size={12.5} />
              </IconButton>
            </Box>
            <Box component="h1" sx={styles.title}>
              {title}
            </Box>
            {hasError && (
              <Box sx={styles.errorContainer}>
                <Box
                  sx={{
                    alignItems: 'center',
                    height: '100%',
                    display: 'flex',
                  }}>
                  <CircleErrorIcon
                    size="4rem"
                    color={tokens.colors.rsmGray.copy}
                    fillColor
                  />
                  <Box sx={{ paddingLeft: '1.5rem' }}>
                    <Box
                      component="span"
                      className="title"
                      sx={styles.errorHeader}
                      data-testid="ReportModal_Error_Header">
                      {t('PowerBI.ReportModal.ErrorHeader')}
                    </Box>
                    <Box
                      sx={styles.errorMessage}
                      data-testid="ReportModal_Error_Message">
                      {t('PowerBI.ReportModal.ErrorMessage')}
                    </Box>
                  </Box>
                </Box>
              </Box>
            )}
            <Box sx={styles.fieldContainer}>
              <TextField
                labelName={t('PowerBI.ReportModal.ReportShortName')}
                fieldName="reportShortName"
                componentName="PowerBIReportModal"
                maxLength={50}
                fieldRequired
                placeholderText={t('PowerBI.ReportModal.Placeholder')}
                fieldValidation={(value) =>
                  stringValidation(
                    value,
                    t('PowerBI.ReportModal.LettersNumbersHyphensError'),
                    letterNumberHyphenRegex,
                  )
                }
                disabled={mode === 'edit' && lockShortNameOnEdit}
              />
              <TextField
                labelName={t('PowerBI.ReportModal.CEMAppId')}
                fieldName="cemAppId"
                componentName="PowerBIReportModal"
                maxLength={50}
                fieldRequired
                placeholderText={t('PowerBI.ReportModal.Placeholder')}
                fieldValidation={(value) =>
                  stringValidation(
                    value,
                    t('PowerBI.ReportModal.LettersNumbersHyphensError'),
                    letterNumberHyphenRegex,
                  )
                }
              />
              <TextField
                labelName={t('PowerBI.ReportModal.TenantId')}
                fieldName="tenantId"
                componentName="PowerBIReportModal"
                fieldRequired
                placeholderText={t('PowerBI.ReportModal.Placeholder')}
                fieldValidation={(value) => guidValidation(value)}
              />
              <TextField
                labelName={t('PowerBI.ReportModal.ServicePrincipalId')}
                fieldName="servicePrincipalId"
                componentName="PowerBIReportModal"
                fieldRequired
                placeholderText={t('PowerBI.ReportModal.Placeholder')}
                fieldValidation={(value) => guidValidation(value)}
              />
              <TextField
                labelName={t('PowerBI.ReportModal.ClientSecretName')}
                fieldName="clientSecretName"
                componentName="PowerBIReportModal"
                maxLength={100}
                fieldRequired
                placeholderText={t('PowerBI.ReportModal.Placeholder')}
                fieldValidation={(value) =>
                  stringValidation(
                    value,
                    t('PowerBI.ReportModal.LettersColonsError'),
                    /^[A-Za-z:]+$/,
                  )
                }
              />
              {renderIsClientLevelAuthorizationCheckbox()}
            </Box>
            <Box sx={styles.buttonContainer}>
              <Button
                variant="outlined"
                data-testid="Btn_AddNewReportDialog_Cancel"
                disabled={isSaving}
                onClick={handleOnClose}>
                {t('PowerBI.ReportModal.Cancel')}
              </Button>
              {mode === 'edit' && (
                <Button
                  variant="outlined"
                  disabled={isSaving}
                  onClick={() => initialData && onRemove?.(initialData.id)}
                  data-testid="Btn_EditReportDialog_RemoveReport">
                  {t('PowerBI.ReportModal.RemoveReport')}
                </Button>
              )}
              <Button
                type="submit"
                variant="contained"
                disabled={isSaving}
                data-testid={
                  mode === 'create'
                    ? 'Btn_AddNewReportDialog_AddReport'
                    : 'Btn_EditReportDialog_Save'
                }>
                {primaryLabel}
              </Button>
            </Box>
          </Box>
        </FormProvider>
      </ResponsiveModal>
    </Box>
  )
}

export default PowerBIReportModal
